import * as Sentry from "@sentry/gatsby";
import { Action } from "re-reduced";
import { SagaIterator } from "redux-saga";
import { call, put, select } from "redux-saga/effects";

import {
  ScanTicketBulk,
  TicketScanDirection
} from "@graphql/sdk";
import actions from "@state/actions";
import {
  getEventId, getGateId, getScanDismissed,
  getGates,
  getScanDirection
} from "@state/selectors";
import { DeviceTicket, ScanPayload } from "@state/types";
import { scanStorage, ticketStorage } from "@util/cache";
import { getTicketScans } from "@util/ticketScans";
import { validateScan } from "@util/validateScan";


export default function* scanDeviceTicket(
  action: Action<ScanPayload>
): SagaIterator {
  yield put(actions.scanDeviceTicket.request());

  try {
    const eventId: string = yield select(getEventId);
    // Match search's read order while sync moves pending scans into tickets.
    const pendingScans: ScanTicketBulk[] = yield call(scanStorage!.values);
    const ticketNumber = action.payload.ticketNumber;
    let foundTicket: DeviceTicket = yield call(ticketStorage!.get, ticketNumber);

    if (!foundTicket) {

      // If the ticket is not found, search the parentTicketNumber because this might be a membership ticket
      // that is being scanned. We need to use a DB search for this as the parentTicketNumber is not indexed.
      const searchFunction = (ticketNumber: string, ticket: DeviceTicket) => {
        return ticket.parentTicketNumber === ticketNumber
      }
      const results = yield call(ticketStorage!.search, ticketNumber, searchFunction);

      if (results?.length > 0) {
        // There can only be 1 ticket per event that references the same parentTicketNumber
        // and because search returns an array, we will grab the first index if it exists.
        foundTicket = results[0];
      } else {

        const errorMsg = "Ticket number not found for this event";
        
        if (!(yield select(getScanDismissed))) {
          yield put(actions.scanTicketOnline.success({ status: "issue", errorMsg }));
        }
        
        Sentry.captureMessage(errorMsg, {
          extra: {
            ticketNumber
          }
        })
        return;
      }
    }

    const allScans = getTicketScans(foundTicket, pendingScans, eventId);

    const scanDirectionState: TicketScanDirection = yield select(getScanDirection);
    const scanDirection = action.payload.direction || scanDirectionState;
    const gateId = yield select(getGateId);
    const gates = yield select(getGates);
    const scanType = action.payload.scanType;

    // Create the new scan object
    const newScan: ScanTicketBulk = {
      eventId,
      gateId,
      ticketNumber,
      scanDirection,
      scanType,
      scannedAt: new Date().toISOString()
    };

    // validate the ticket status
    const { status, message } = validateScan({
      ticket: foundTicket,
      scan: newScan,
      allScans,
      gates,
      isOnline: false
    })

    if (status === "success") {
      const scanEntries: [IDBValidKey, ScanTicketBulk][] = [
        [newScan.scannedAt, newScan],
      ];
      yield call(scanStorage!.setMany, scanEntries);
      pendingScans.push(newScan);
      yield put(actions.updatePendingScanCount());
    }

    if (!(yield select(getScanDismissed))) {
      yield put(actions.scanTicketOnline.success({
        errorMsg: message,
        ticket: {
          ...foundTicket,
          scans: getTicketScans(foundTicket, pendingScans, eventId),
        },
        status,
      }));
    }
  } catch (error) {
    Sentry.captureException(error);
    const message = "Could not save scan on this device. Please try again.";
    if (!(yield select(getScanDismissed))) {
      yield put(actions.scanTicketOnline.failure(message));
    }
  }
}
